/**
 * Browser-engine picker using the same selectable radio-card interaction as
 * the visual viewport picker.
 *
 * @module ui/components/BrowserEnginePicker
 */

import React from 'react';
import { Radio, RadioGroup } from 'react-aria-components';
import { Compass, Fire, GoogleChromeLogo } from '@phosphor-icons/react';
import { cn } from '@chatsdet/ui';
import {
  BROWSER_ENGINE_LABELS,
  BROWSER_ENGINES,
  isBrowserEngine,
  type BrowserEngine,
} from '@chatsdet/types';

type BrowserEngineIcon = React.ComponentType<{
  size?: number;
  weight?: 'regular' | 'fill' | 'duotone';
  className?: string;
  'aria-hidden'?: boolean;
}>;

const ENGINE_ICONS: Record<BrowserEngine, BrowserEngineIcon> = {
  chromium: GoogleChromeLogo,
  firefox: Fire,
  webkit: Compass,
};

const ENGINE_ICON_COLORS: Record<BrowserEngine, string> = {
  chromium: 'text-blue-500',
  firefox: 'text-orange-500',
  webkit: 'text-blue-600',
};

export interface BrowserEnginePickerProps {
  value: BrowserEngine;
  onChange: (value: BrowserEngine) => void;
  isDisabled?: boolean;
  /** Accessible label for the radio group. */
  'aria-label'?: string;
}

export function BrowserEnginePicker({
  value,
  onChange,
  isDisabled,
  'aria-label': ariaLabel = 'Browser engine',
}: BrowserEnginePickerProps) {
  return (
    <RadioGroup
      aria-label={ariaLabel}
      value={value}
      onChange={(next) => {
        if (isBrowserEngine(next)) onChange(next);
      }}
      isDisabled={isDisabled}
      className="flex flex-wrap gap-2"
    >
      {BROWSER_ENGINES.map((engine) => {
        const Icon = ENGINE_ICONS[engine];
        return (
          <Radio
            key={engine}
            value={engine}
            className={({ isSelected, isFocusVisible, isDisabled: itemDisabled }) =>
              cn(
                'flex w-[8.5rem] cursor-pointer flex-col items-center gap-1.5 rounded-lg border px-3 py-3 text-center transition-colors',
                'hover:bg-muted/40',
                isSelected
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border bg-card',
                isFocusVisible && 'outline-none ring-2 ring-primary ring-offset-1',
                itemDisabled && 'cursor-not-allowed opacity-50'
              )
            }
          >
            {({ isSelected }) => (
              <>
                <Icon
                  size={26}
                  weight={isSelected ? 'fill' : 'regular'}
                  className={ENGINE_ICON_COLORS[engine]}
                  aria-hidden
                />
                <span className="text-sm font-medium text-foreground">
                  {BROWSER_ENGINE_LABELS[engine]}
                </span>
                <span className="text-xs text-muted-foreground">
                  {engine === 'chromium'
                    ? 'Blink'
                    : engine === 'firefox'
                      ? 'Gecko'
                      : 'Safari engine'}
                </span>
              </>
            )}
          </Radio>
        );
      })}
    </RadioGroup>
  );
}
